// Single source of truth for HR drill-down dimensions.
// MUST stay aligned with `HR_COLUMN_MAP` in
// `business-dashboard-backend/app/core/database/hr_stats.py`.

import type { HrDimension } from '../types/hrStats';

export interface HrDimensionMeta {
  /** Backend dimension key. */
  id: HrDimension;
  /** Display label shown in dropdowns / breadcrumb chips. */
  label: string;
  /** Short description used in the dimension picker. */
  description?: string;
  /** Whether this dimension typically yields a small enum (good for chart). */
  smallCardinality?: boolean;
}

export const HR_DIMENSIONS: HrDimensionMeta[] = [
  { id: 'location', label: 'Location', description: 'City / office location' },
  { id: 'country', label: 'Country', smallCardinality: true },
  { id: 'business_unit', label: 'Business Unit' },
  { id: 'sub_bu', label: 'Sub-BU' },
  { id: 'pillar', label: 'Pillar', smallCardinality: true },
  { id: 'designation', label: 'Designation' },
  { id: 'employee_type', label: 'Employee Type', smallCardinality: true },
  { id: 'gender', label: 'Gender', smallCardinality: true },
  { id: 'status', label: 'Status', smallCardinality: true },
  { id: 'work_mode', label: 'Work Mode', smallCardinality: true },
  { id: 'company', label: 'Company' },
  { id: 'manager', label: 'Reporting Manager' },
  { id: 'hr_manager', label: 'HR Manager' },
  { id: 'bu_head', label: 'BU Head' },
  { id: 'tenure_bucket', label: 'Tenure', smallCardinality: true },
];

const HR_DIMENSION_BY_ID: Record<HrDimension, HrDimensionMeta> = HR_DIMENSIONS.reduce(
  (acc, d) => {
    acc[d.id] = d;
    return acc;
  },
  {} as Record<HrDimension, HrDimensionMeta>,
);

export function getHrDimensionMeta(id: HrDimension): HrDimensionMeta {
  return HR_DIMENSION_BY_ID[id] ?? { id, label: id };
}

/** Default landing dimension when no group_by is in the URL. */
export const DEFAULT_HR_DIMENSION: HrDimension = 'location';

/**
 * Suggested progression when the user clicks a row to drill in.
 * The next dimension is the first entry in this list that the user has NOT
 * already filtered on. Users can override via the dimension dropdown.
 */
export const DEFAULT_DRILL_ORDER: HrDimension[] = [
  'location',
  'pillar',
  'business_unit',
  'sub_bu',
  'designation',
  'employee_type',
  'gender',
  'tenure_bucket',
];

/** Pick the next sensible drill dimension given the active filters. */
export function nextDrillDimension(
  current: HrDimension,
  activeFilters: Partial<Record<HrDimension, string | string[]>>,
): HrDimension | null {
  const usedKeys = new Set<HrDimension>(Object.keys(activeFilters) as HrDimension[]);
  usedKeys.add(current);
  for (const dim of DEFAULT_DRILL_ORDER) {
    if (!usedKeys.has(dim)) return dim;
  }
  // Fallback: any dimension not yet used.
  for (const meta of HR_DIMENSIONS) {
    if (!usedKeys.has(meta.id)) return meta.id;
  }
  return null;
}
